/**
 * Checkbox primitive (Round 9 Item 51, 2026-06-25).
 *
 * Custom checkbox replacing OS-default `<input type="checkbox">` across the
 * app. Native checkboxes leak OS-level styling (Aqua-blue on macOS, a flat
 * blue square on Windows) that fights the Olytic teal/obsidian palette;
 * this primitive gives every matrix/list checkbox the same teal-fill,
 * 4px-corner, stroke-dasharray-animated checkmark.
 *
 * Use Checkbox for binary selection in a matrix/list (one of many — bulk-
 * select tables, multi-select recipient pickers, per-row notification
 * matrices). For a unary on/off setting (a single boolean preference like
 * "Confirm writes in chat" or "Auto-assign new members") use the Toggle
 * primitive (Round 10 candidate — not yet built; falls back to this
 * Checkbox in the interim).
 */

import { useEffect, useRef } from 'react';

interface CheckboxProps {
  /**
   * Current checked state. Controlled component — parent owns the boolean.
   */
  checked: boolean;
  /**
   * Called with the next boolean when the user clicks/keys the control.
   * Matrix/list pattern: parent updates a Set<id> or boolean field.
   */
  onChange: (next: boolean) => void;
  /**
   * When true, opacity 0.5 + cursor not-allowed + no interaction.
   */
  disabled?: boolean;
  /**
   * Accessible label — required for standalone Checkbox (no visible
   * label-for sibling). When wrapped in a `<label>` parent the accessible
   * name comes from the label text and this prop can be omitted.
   */
  'aria-label'?: string;
  /**
   * Indeterminate (mixed) state — renders a horizontal bar instead of the
   * checkmark; same teal fill. Useful for "select all" headers where some
   * but not all rows are selected.
   */
  indeterminate?: boolean;
  /**
   * 'md' (default) = 18px box. 'sm' = 16px box for dense list rows.
   */
  size?: 'sm' | 'md';
  /**
   * Optional DOM id — pair with a separate `<label htmlFor>` when needed.
   */
  id?: string;
}

export function Checkbox({
  checked,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
  indeterminate = false,
  size = 'md',
  id,
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Sync indeterminate (DOM-only state, not a React attr) on every render
  // that changes the prop.
  useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const box = size === 'sm' ? 16 : 18;

  return (
    <span
      className={[
        'olytic-checkbox',
        checked ? 'is-checked' : '',
        indeterminate ? 'is-indeterminate' : '',
        disabled ? 'is-disabled' : '',
        size === 'sm' ? 'is-sm' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ width: box, height: box }}
    >
      <input
        ref={inputRef}
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-checked={indeterminate ? 'mixed' : checked}
        className="olytic-checkbox-input"
      />
      <span className="olytic-checkbox-box" aria-hidden="true">
        {indeterminate ? (
          <svg
            viewBox="0 0 18 18"
            width={box}
            height={box}
            className="olytic-checkbox-bar"
          >
            <line
              x1="4"
              y1="9"
              x2="14"
              y2="9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg
            viewBox="0 0 18 18"
            width={box}
            height={box}
            className="olytic-checkbox-check"
          >
            <path
              d="M4 9.5 L7.5 13 L14 5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </span>
  );
}
